/**
 * python-compat.js — 파이썬 탭 `Run`(jspython) 경로의 문법·내장함수 보정.
 *
 * 왜: jspython 원 엔진에는 len/str/int 같은 파이썬 내장이 하나도 없고,
 *     `x += 1`·`pass`·열0 주석은 파서가 거부한다 (2026-09-07 실측 —
 *     static/jspython-interpreter.min.js 로 문법 46개 실행, 내장함수 전부 오류).
 *     아티보 에디터(CCWEB pythoncode_func.js, 2026-08-27)에서 검증된 보정을 그대로 이식.
 * 비용: Run 버튼을 누를 때만 실행. 줄 단위 치환이라 줄 수가 보존되어 오류 줄번호가 학생 코드와 일치.
 * 되돌리기: pythoncode-tab.jsx 의 import 한 줄과 호출 두 곳을 지우면 끝 (이 파일은 단독 모듈).
 * 안 건드리는 것: Pyodide(`Python 3`) 경로, 하드웨어 함수 등록부.
 */

/**
 * 열0(들여쓰기 없는) 순수 주석 줄을 빈 줄로 바꾼다.
 * jspython 파서는 if 블록과 else/elif 사이에 열0 주석이 있으면 블록 연결을 잃는다.
 * 삼중따옴표 문자열 안의 `#` 줄은 데이터이므로 건드리지 않는다.
 * @param {string} src 파이썬 소스
 * @returns {string} 줄 수가 같은 소스
 */
const blankColumnZeroComments = src => {
    const lines = String(src).split('\n');
    let inTriple = false;
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        if (!inTriple && line.charAt(0) === '#') {
            lines[i] = '';
            continue;
        }
        const m = line.match(/"""|'''/g);
        if (m && (m.length % 2 === 1)) inTriple = !inTriple;
    }
    return lines.join('\n');
};

/**
 * Run 직전 소스 전처리: 열0 주석 → 빈 줄, `x += 1` → `x = x + (1)`, `pass` → `None`.
 * 실패하면 원본을 그대로 돌려준다 (전처리 때문에 실행이 막히는 일은 없게).
 * @param {string} src 파이썬 소스
 * @returns {string} 전처리된 소스 (줄 수 동일)
 */
const preprocessPython = src => {
    if (typeof src !== 'string') return src;
    try {
        const lines = blankColumnZeroComments(src).split('\n');
        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            // x += 1  ->  x = x + (1)   (단순 식별자 대상만 — d["k"] += 같은 복합 대상은 미변환)
            const m = line.match(/^(\s*)([A-Za-z_가-힣][A-Za-z0-9_가-힣]*)\s*([+\-*/])=\s*(.+)$/);
            if (m) {
                lines[i] = `${m[1]}${m[2]} = ${m[2]} ${m[3]} (${m[4]})`;
                continue;
            }
            // pass -> None (파서가 pass 를 변수로 취급해 죽는다)
            if (/^(\s*)pass\s*$/.test(line)) { lines[i] = line.replace(/pass\s*$/, 'None'); continue; }
            // def -> async def (2026-10-06 직원 실측: 함수 안의 sleep·모터 명령을 엔진이 기다리지 않아 함수가 통째로 한꺼번에 실행됐다.
            //   async def 면 한 줄씩 기다린다. 값 돌려주기·재귀·반복/조건 안에서 부르기 모두 그대로 동작 확인)
            if (/^(\s*)def\s/.test(line)) lines[i] = line.replace(/^(\s*)def\s/, '$1async def ');
        }
        return lines.join('\n');
    } catch (e) {
        return src;
    }
};

const toArray = args => (
    (args.length === 1 && Array.isArray(args[0])) ? args[0] : Array.prototype.slice.call(args)
);

const defineIfMissing = (proto, name, fn) => {
    try {
        if (!proto[name]) {
            Object.defineProperty(proto, name, {value: fn, enumerable: false, configurable: true, writable: true});
        }
    } catch (e) {
        // 프로토타입이 잠겨 있으면 그 메서드만 포기한다 — 나머지 보정은 계속.
    }
};

/**
 * 파이썬 내장(len/str/int/…)과 문자열·리스트 메서드(.upper/.append/…)를 채운다.
 * 내장은 assignGlobalContext 의 "동기 함수"로 넣는다 — addFunction(async) 과 달리
 * def 함수 안에서도 값이 즉시 돌아온다. 메서드는 프로토타입에 non-enumerable 로,
 * 이미 있으면 건드리지 않는다 (여러 번 호출해도 안전).
 * @param {object} interpreter jspython 인터프리터 인스턴스
 */
const installPythonBuiltins = interpreter => {
    interpreter.assignGlobalContext({
        True: true,
        False: false,
        None: null,
        len: x => {
            try {
                return (x && x.length !== null && typeof x.length !== 'undefined') ? x.length : Object.keys(x).length;
            } catch (e) {
                return 0;
            }
        },
        str: x => ((x === null || typeof x === 'undefined') ? 'None' : String(x)),
        int: x => {
            const n = parseInt(x, 10);
            return isNaN(n) ? 0 : n;
        },
        float: x => {
            const n = parseFloat(x);
            return isNaN(n) ? 0 : n;
        },
        abs: x => Math.abs(x),
        round: (x, d) => {
            const p = Math.pow(10, d || 0);
            return Math.round(x * p) / p;
        },
        sum: a => (Array.isArray(a) ? a : []).reduce((s, v) => s + v, 0),
        max: (...args) => Math.max.apply(null, toArray(args)),
        min: (...args) => Math.min.apply(null, toArray(args)),
        sorted: a => (Array.isArray(a) ? a.slice().sort((x, y) => (x < y ? -1 : (x > y ? 1 : 0))) : a),
        // 파서가 not 을 식별자 취급한다 — 함수로 채우면 not(조건) 형태가 동작한다.
        // 괄호 없는 `not x` 는 여전히 파서 미지원 (레퍼런스 부록 B 에 문서화).
        not: x => !x
    });
    // 프로토타입 메서드는 호출 대상(this)이 곧 파이썬 값이라 `this` 가 필수 — 이 블록만 규칙 해제.
    /* eslint-disable no-invalid-this */
    defineIfMissing(String.prototype, 'upper', function () {
        return this.toUpperCase();
    });
    defineIfMissing(String.prototype, 'lower', function () {
        return this.toLowerCase();
    });
    defineIfMissing(String.prototype, 'strip', function () {
        return this.trim();
    });
    defineIfMissing(String.prototype, 'join', function (arr) {
        return Array.isArray(arr) ? arr.join(String(this)) : String(this);
    });
    defineIfMissing(String.prototype, 'format', function (...args) {
        let i = 0;
        return String(this).replace(/\{\}/g, () => args[i++]);
    });
    defineIfMissing(Array.prototype, 'append', function (v) {
        this.push(v);
        return null;
    });
    defineIfMissing(Array.prototype, 'insert', function (i, v) {
        this.splice(i, 0, v);
        return null;
    });
    defineIfMissing(Array.prototype, 'remove', function (v) {
        const i = this.indexOf(v);
        if (i >= 0) this.splice(i, 1);
        return null;
    });
    /* eslint-enable no-invalid-this */
};

export {
    preprocessPython,
    installPythonBuiltins
};
